import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, MessageCircle, Users, X } from 'lucide-react';
import { API } from '../../../lib/api';
import { greetOrName, renderMessage, waUrl } from '../../../lib/waShare';

const short = (w) => w.replace(/:00/g, '').replace('-', '–');

// Customers due for a service, each with a one-tap WhatsApp offer for the free slots.
export const FillSlotDialog = ({ alert, authHeader, onClose }) => {
  const [months, setMonths] = useState(5);
  const [data, setData] = useState(null);
  const [sent, setSent] = useState([]);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/appointments/due-customers`, { headers: authHeader(), params: { months } })
      .then(({ data: d }) => setData(d)).catch(() => setData({ rows: [], total: 0 }));
  }, [months, authHeader]);
  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const day = new Date(`${alert.date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  const slots = `${day}: ${alert.rows.filter((r) => r.free > 0).map((r) => short(r.window)).join(', ')}`;
  const msg = (c) => renderMessage('slot_offer', {
    customer: greetOrName(c.prefix, c.name), vehicle: c.vehicle || 'car', slots, book_link: `${window.location.origin}/book`,
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} data-testid="fill-slot-dialog">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 p-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Users size={17} className="text-teal-700" /> Fill {day}</h2>
            <p className="mt-0.5 text-xs text-slate-500">Customers not seen for a while, with nothing booked. Free: {slots.split(': ')[1] || 'none'}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="fill-slot-close"><X size={18} className="text-slate-500" /></button>
        </div>
        <div className="flex items-center gap-2 px-4 pt-3 text-xs text-slate-600">
          Last visit
          <select className="rounded-md border border-slate-300 px-2 py-1" value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="fill-slot-months">
            {[3, 4, 5, 6, 9, 12].map((m) => <option key={m} value={m}>{m}+ months ago</option>)}
          </select>
          {data && <span className="ml-auto font-semibold" data-testid="fill-slot-total">{data.total} customer{data.total === 1 ? '' : 's'}</span>}
        </div>
        <ul className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="fill-slot-list">
          {data === null && <li><Loader2 className="animate-spin text-slate-400" size={16} /></li>}
          {data?.rows.length === 0 && <li className="text-sm text-slate-500" data-testid="fill-slot-empty">Nobody is due for a visit right now.</li>}
          {(data?.rows || []).map((c) => (
            <li key={c.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`fill-slot-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">{[c.prefix, c.name].filter(Boolean).join(' ')} <span className="font-mono text-xs font-normal text-slate-500">{c.phone}</span></p>
                <p className="truncate text-xs text-slate-500">{c.vehicle || 'No vehicle on file'} · last visit {new Date(`${c.last_visit}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
              <a href={waUrl(c.phone, msg(c))} target="_blank" rel="noopener noreferrer" onClick={() => setSent((s) => [...s, c.id])} data-testid={`fill-slot-wa-${c.id}`}
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${sent.includes(c.id) ? 'border border-emerald-300 bg-emerald-50 text-emerald-800' : 'bg-emerald-600'}`}
                style={sent.includes(c.id) ? undefined : { color: '#fff' }}>
                <MessageCircle size={13} /> {sent.includes(c.id) ? 'Sent' : 'Offer'}
              </a>
            </li>
          ))}
        </ul>
        <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">Edit the wording under Settings › Customer messages › Free-slot offer.</p>
      </div>
    </div>
  );
};
